// ── Backtest v2 — Rich results, multi-strategy, GQ AI ─────────────────

// BT_STRATS removed — replaced with live Supabase strategies
const BT_PRESETS = [
  {label:"Quick 1Y",from:"2024-01-01",to:"2024-12-31"},
  {label:"Full 5Y",from:"2020-01-01",to:"2024-12-31"},
  {label:"Bull run",from:"2020-10-01",to:"2021-11-01"},
  {label:"Bear 2022",from:"2022-01-01",to:"2022-12-31"},
];

// Synthetic equity used by the Compare tab placeholder benchmark only.
function genEquity(seed,len){const d=[];let v=10000;for(let i=0;i<len;i++){v=v*(1+(Math.sin(i*seed+13)*.5+.5-.44)*.025);d.push(v);}return d;}

// ── Expandable Section ────────────────────────────────────────────────
function Expandable({title,defaultOpen=false,children,badge}){
  const [open,setOpen]=React.useState(defaultOpen);
  return(
    <div className="card" style={{overflow:"hidden"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",padding:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span className="card-title" style={{margin:0}}>{title}</span>
          {badge&&<span className="badge badge-muted" style={{fontSize:9}}>{badge}</span>}
        </div>
        <ChevronDown size={14} color="var(--muted)" style={{transform:open?"rotate(180deg)":"none",transition:"transform .2s"}}/>
      </button>
      {open&&<div style={{marginTop:14}}>{children}</div>}
    </div>
  );
}

// ── Walk-forward panel: per-fold train vs test Sharpe ────────────────
function WalkForwardPanel({ wf }) {
  const ref = React.useRef(null);
  const folds = wf?.folds || [];
  const avgDelta = folds.length ? folds.reduce((a, f) => a + (f.deltaSharpe || 0), 0) / folds.length : 0;
  React.useEffect(() => {
    if (!ref.current || !window.Plotly || !folds.length) return;
    const x = folds.map(f => `Fold ${f.fold + 1}`);
    const trainY = folds.map(f => f.train?.metrics?.sharpe || 0);
    const testY  = folds.map(f => f.test?.metrics?.sharpe || 0);
    window.Plotly.newPlot(ref.current, [
      { x, y: trainY, name: "In-sample (train)",  type: "bar", marker: { color: "#3b9eff" } },
      { x, y: testY,  name: "Out-of-sample (test)", type: "bar", marker: { color: "#11ff99" } },
    ], {
      barmode: "group",
      paper_bgcolor: "rgba(0,0,0,0)", plot_bgcolor: "rgba(0,0,0,0)",
      font: { family: "Inter,sans-serif", color: "#f5f5f7", size: 11 },
      margin: { l: 44, r: 12, t: 12, b: 32 },
      xaxis: { gridcolor: "rgba(255,255,255,0.05)" },
      yaxis: { gridcolor: "rgba(255,255,255,0.05)", title: { text: "Sharpe", font: { size: 10 } } },
      legend: { orientation: "h", y: -0.18, font: { size: 10 } },
      height: 220,
    }, { displayModeBar: false, responsive: true });
    return () => { try { window.Plotly.purge(ref.current); } catch {} };
  }, [folds]);
  if (!folds.length) return null;
  return (
    <div className="card">
      <div className="card-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>Walk-forward analysis · {folds.length} folds</span>
        {wf.overfittingFlag ? (
          <span className="badge badge-red" style={{ fontSize: 10 }}>Overfit risk · avg ΔSharpe {avgDelta.toFixed(2)}</span>
        ) : (
          <span className="badge badge-green" style={{ fontSize: 10 }}>OOS robust · avg ΔSharpe {avgDelta.toFixed(2)}</span>
        )}
      </div>
      {wf.overfittingFlag && (
        <div style={{
          background: "rgba(255,32,71,0.06)", border: "1px solid rgba(255,32,71,0.25)",
          borderRadius: "var(--r-md)", padding: "8px 12px", marginBottom: 12,
          fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--red)", lineHeight: 1.5,
        }}>
          <b>Warning · in-sample Sharpe exceeds out-of-sample by &gt; 0.5 on average.</b> The strategy likely overfits to the training period. Consider widening the parameter search, simplifying entry rules, or training on a longer window.
        </div>
      )}
      <div ref={ref} style={{ width: "100%", height: 220 }}/>
      <div style={{ marginTop: 12, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {["Fold", "Train Sharpe", "Test Sharpe", "Δ", "Train return", "Test return"].map(h => (
                <th key={h} style={{ fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--muted)", textAlign: "left", padding: "4px 8px", paddingBottom: 8, fontWeight: 500, letterSpacing: ".04em" }}>{h.toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {folds.map(f => {
              const tr = f.train?.metrics || {}, te = f.test?.metrics || {};
              const d = f.deltaSharpe || 0;
              return (
                <tr key={f.fold} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)", padding: "6px 8px" }}>{f.fold + 1}</td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "#3b9eff", padding: "6px 8px" }}>{(tr.sharpe || 0).toFixed(2)}</td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "#11ff99", padding: "6px 8px" }}>{(te.sharpe || 0).toFixed(2)}</td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: d > 0.5 ? "var(--red)" : d > 0 ? "var(--orange)" : "var(--green)", padding: "6px 8px" }}>{d >= 0 ? "+" : ""}{d.toFixed(2)}</td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)", padding: "6px 8px" }}>{((tr.totalReturn || 0) * 100).toFixed(1)}%</td>
                  <td style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)", padding: "6px 8px" }}>{((te.totalReturn || 0) * 100).toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Rolling chart ─────────────────────────────────────────────────────
function RollingChart({data,label,color="#11ff99",height=180}){
  const ref=React.useRef(null);
  React.useEffect(()=>{
    if(!ref.current||!window.Plotly)return;
    window.Plotly.newPlot(ref.current,[{x:data.map((_,i)=>i),y:data,type:"scatter",mode:"lines",line:{color,width:1.5,shape:"spline"},fill:"tozeroy",fillcolor:color.replace(")",",0.08)").replace("rgb","rgba"),hovertemplate:`${label}: <b>%{y:.2f}</b><extra></extra>`}],{paper_bgcolor:"rgba(0,0,0,0)",plot_bgcolor:"rgba(0,0,0,0)",font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},margin:{l:40,r:10,t:10,b:28},xaxis:{gridcolor:"rgba(255,255,255,0.05)",tickfont:{family:"Geist Mono,monospace",size:9,color:"rgba(245,245,247,0.5)"}},yaxis:{gridcolor:"rgba(255,255,255,0.05)",tickfont:{family:"Geist Mono,monospace",size:9,color:"rgba(245,245,247,0.5)"}},height},{displayModeBar:false,responsive:true});
    return()=>{try{window.Plotly.purge(ref.current)}catch{}};
  },[data]);
  return<div ref={ref} style={{width:"100%",height}}/>;
}

// ── Win/Loss Streaks ──────────────────────────────────────────────────
function StreakChart({trades, height=140}){
  const streaks = React.useMemo(() => {
    if (trades && trades.length >= 2) {
      const result = []; let cur = 0;
      for (const t of trades) {
        const win = (t.pnl || 0) > 0;
        if (cur === 0) { cur = win ? 1 : -1; }
        else if ((cur > 0) === win) { cur = cur > 0 ? cur + 1 : cur - 1; }
        else { result.push(cur); cur = win ? 1 : -1; }
      }
      if (cur !== 0) result.push(cur);
      return result.slice(-20);
    }
    const rng = window.seededRng ? window.seededRng(42) : (() => Math.random());
    return Array.from({length:20}, () => Math.floor(rng() * 8) - 3);
  }, [trades]);
  const ref=React.useRef(null);
  React.useEffect(()=>{
    if(!ref.current||!window.Plotly)return;
    window.Plotly.newPlot(ref.current,[{x:streaks.map((_,i)=>i+1),y:streaks,type:"bar",marker:{color:streaks.map(v=>v>=0?"rgba(17,255,153,0.6)":"rgba(255,32,71,0.6)")},hovertemplate:"Streak %{x}: <b>%{y}</b><extra></extra>"}],{paper_bgcolor:"rgba(0,0,0,0)",plot_bgcolor:"rgba(0,0,0,0)",font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},margin:{l:30,r:10,t:10,b:28},xaxis:{gridcolor:"rgba(255,255,255,0.04)"},yaxis:{gridcolor:"rgba(255,255,255,0.04)",zeroline:true,zerolinecolor:"rgba(255,255,255,0.1)"},height,bargap:0.3},{displayModeBar:false,responsive:true});
    return()=>{try{window.Plotly.purge(ref.current)}catch{}};
  },[streaks]);
  return<div ref={ref} style={{width:"100%",height}}/>;
}

// ── Main Backtest Page ────────────────────────────────────────────────
function BacktestPage({onToast,onNavigate}){
  const narrow = window.useNarrow ? window.useNarrow() : false;
  const [userStrategies, setUserStrategies] = React.useState([]);
  const [strategyId, setStrategyId] = React.useState(null); // Supabase strategy UUID
  const [strategy, setStrategy] = React.useState("My Strategy");
  const [strategySpec, setStrategySpec] = React.useState(null); // engine spec from designer
  const [asset,setAsset]=React.useState("SPY");
  const [from,setFrom]=React.useState("2021-01-04");
  const [to,setTo]=React.useState("2025-10-31");

  // On mount: load user strategies + hydrate from URL run param
  React.useEffect(() => {
    const init = async () => {
      const sb = window.sbClient;
      const state = window.GQ?.state;
      // Load user strategies for the selector
      if (sb) {
        try {
          const { data: { user } } = await sb.auth.getUser();
          if (user) {
            const { data: strats } = await sb.from('strategies').select('id,name,strategy_type,current_params').eq('user_id', user.id).order('created_at', { ascending: false });
            if (strats?.length) setUserStrategies(strats);
          }
        } catch {}
      }
      // Pre-select from pipeline state
      const cur = state?.getCurrentStrategy();
      if (cur) {
        setStrategyId(cur.id);
        setStrategy(cur.name || "My Strategy");
        if (cur.current_params) setStrategySpec(cur.current_params);
      }
      // Hydrate from ?run= URL param (navigated from Designer "Full report")
      if (state?.parseHash) {
        const { params } = state.parseHash();
        if (params.run) {
          const run = await state.fetchBacktestRun(params.run);
          if (run) {
            const eng = run.equity_curve || [];
            const mets = run.metrics || {};
            const trades = run.trades || [];
            setResults({
              totalReturn: mets.totalReturn || 0, cagr: mets.cagr || 0,
              sharpe: mets.sharpe || 0, sortino: mets.sortino || 0,
              maxDD: Math.abs(mets.maxDrawdown || 0), winRate: mets.winRate || 0,
              trades: trades.length, profitFactor: mets.profitFactor || 0,
              calmar: mets.calmar || 0, var95: mets.var95 || 0, cvar95: mets.cvar95 || 0,
              psr: mets.psr || 0, ulcerIndex: mets.ulcerIndex || 0,
              equity: eng.map(p => p.v || p),
              benchmark: null, dds: [], monthly: {}, returns: [], rollingSharpe: [], rollingVol: [],
              tradeLog: trades.slice(-30).reverse().map((t, i) => [
                String(trades.length - i), (t.exitTime || '').slice(0, 10),
                t.side === 'long' ? 'Long' : 'Short',
                (t.entryPx || 0).toFixed(2), (t.exitPx || 0).toFixed(2),
                (t.pnl > 0 ? '+' : '') + (t.pnl || 0).toFixed(2),
                (t.pnlPct > 0 ? '+' : '') + ((t.pnlPct || 0) * 100).toFixed(2) + '%',
                (t.holdBars || 0) + 'd',
              ]),
              rawTrades: trades, walkForward: run.config?.walk_forward || null, basket: null,
              _loadedRunId: params.run,
            });
            if (run.strategy_id) setStrategyId(run.strategy_id);
          }
        }
      }
    };
    init();
  }, []);

  // Available symbols: bundled CSVs + any uploaded via Settings → Data
  const BUNDLED_SYMBOLS = ["SPY","QQQ","AAPL","MSFT","GLD","TLT"];
  const availableSymbols = React.useMemo(() => {
    const uploaded = Object.keys(window.GQ_DATA_CACHE || {});
    return [...new Set([...BUNDLED_SYMBOLS, ...uploaded])];
  }, [strategy /* re-eval when page state changes */]);
  const [capital,setCapital]=React.useState(10000);
  const [fees,setFees]=React.useState(0.05);
  const [slippage,setSlippage]=React.useState(0.02);
  const [running,setRunning]=React.useState(false);
  const [results,setResults]=React.useState(null);
  const [showAdvanced,setShowAdvanced]=React.useState(false);
  const [aiSummary,setAiSummary]=React.useState(null);
  const [aiLoading,setAiLoading]=React.useState(false);
  const [compareMode,setCompareMode]=React.useState(false);
  const [compareStrats,setCompareStrats]=React.useState(["ETH Alpha"]);
  const [resultTab,setResultTab]=React.useState("overview");
  // Walk-forward
  const [wfEnabled,setWfEnabled]=React.useState(false);
  const [wfFolds,setWfFolds]=React.useState(5);
  const [wfTrainPct,setWfTrainPct]=React.useState(0.7);
  // Multi-asset basket (Item E)
  const [basketMode,setBasketMode]=React.useState(false);
  const [basketSymbols,setBasketSymbols]=React.useState(["SPY","QQQ","GLD"]);
  const [basketWeights,setBasketWeights]=React.useState({SPY:0.4,QQQ:0.4,GLD:0.2});
  const [basketRebalance,setBasketRebalance]=React.useState("monthly");
  const [compareResults, setCompareResults] = React.useState({});
  const [compareRunning, setCompareRunning] = React.useState(false);

  const run = async () => {
    setRunning(true); setResults(null); setAiSummary(null);
    try {
      const { engine, data, state, stats } = window.GQ;
      // Asset is selected explicitly in the picker; data.load reads CSV first, falls back to synthetic
      const bars = await data.load(asset, { bars: 1260 });
      const slice = data.sliceByDate(bars, from, to);
      const useBars = slice.length > 30 ? slice : bars;

      // Use spec from designer/optimizer if available; otherwise EMA crossover default
      const defaultSpec = {
        indicators: [
          { id:'ef',    type:'ema', source:'close', params:{period:9} },
          { id:'es',    type:'ema', source:'close', params:{period:21} },
          { id:'rsi14', type:'rsi', source:'close', params:{period:14} },
          { id:'atr14', type:'atr', source:'close', params:{period:14} },
        ],
        entry: { long: ['ef > es and rsi14 > 50'] },
        exit:  { long: ['ef < es'] },
        sizing: { mode:'atrTarget', atrK:2, riskPct:0.01 },
        execution: { orderType:'market' },
        constraints: { allowShort:false },
      };
      const spec = {
        ...(strategySpec || window.GQ?.state?.getCurrentStrategy()?.current_params || defaultSpec),
        costs: { commissionBps: fees * 100, slippageBps: slippage * 100, slippageModel:'volImpact' },
      };
      // Branch: single asset (engine.run) vs basket (engine.runPortfolio)
      let r, bench;
      if (basketMode && basketSymbols.length > 1) {
        // Load each symbol & align (trim to shortest)
        const loaded = {};
        for (const sym of basketSymbols) {
          const sb = await data.load(sym, { bars: 1260 });
          loaded[sym] = data.sliceByDate(sb, from, to).length > 30
            ? data.sliceByDate(sb, from, to) : sb;
        }
        const minLen = Math.min(...Object.values(loaded).map(b => b.length));
        const aligned = {}; for (const k of Object.keys(loaded)) aligned[k] = loaded[k].slice(-minLen);
        // Normalise weights so they sum to 1.0 even if user typed slightly off
        const wSum = basketSymbols.reduce((a,s)=>a+(basketWeights[s]||0),0) || 1;
        const w = basketSymbols.map(s => (basketWeights[s]||0) / wSum);
        const specWithW = { ...spec, portfolio: { weights: w } };
        const port = engine.runPortfolio(specWithW, aligned, { capital: +capital });
        const eq = port.equity_curve;
        // Re-derive missing fields the UI expects
        const equityVals = eq.map(p => p.v);
        const retsP = stats.equityToReturns(equityVals);
        r = {
          equity_curve: eq,
          trades: [],
          metrics: { ...port.metrics, tradeCount: 0 },
          strategy_snapshot: specWithW, config: { basket: { symbols: basketSymbols, weights: w, rebalance: basketRebalance } },
        };
        bench = data.buyAndHold(aligned[basketSymbols[0]], +capital).equity;
      } else {
        r = engine.run(spec, useBars, { capital: +capital, seed: 42 });
        bench = data.buyAndHold(useBars, +capital).equity;
      }
      // Walk-forward (out-of-sample cross-validation)
      let walkForward = null;
      if (wfEnabled && !basketMode) {
        try {
          walkForward = engine.walkForward(spec, useBars, { folds: +wfFolds, trainPct: +wfTrainPct });
        } catch (e) { console.warn("[backtest.walkForward]", e); }
      }
      // Rolling Sharpe + Vol
      const rets = stats.equityToReturns(r.equity_curve.map(p => p.v));
      const rollingSharpe = stats.rollingSharpe(rets, 60).filter(v => v !== null);
      const rollingVol = stats.rollingVol(rets, 60).filter(v => v !== null).map(v => v * 100);
      // Monthly heatmap data
      const monthly = {};
      const eq = r.equity_curve;
      let lastKey = null, periodStart = eq[0]?.v;
      for (const p of eq) {
        const key = (p.t || '').slice(0, 7);
        if (lastKey && key !== lastKey) {
          monthly[lastKey] = ((p.v / periodStart) - 1) * 100;
          periodStart = p.v;
        }
        lastKey = key;
      }
      // Trade log (already produced by engine)
      const tradeLog = r.trades.slice(-30).reverse().map((t, i) => [
        String(r.trades.length - i),
        (t.exitTime || '').slice(0, 10),
        t.side === 'long' ? 'Long' : 'Short',
        t.entryPx.toFixed(2),
        t.exitPx.toFixed(2),
        (t.pnl > 0 ? '+' : '') + t.pnl.toFixed(2),
        (t.pnlPct > 0 ? '+' : '') + (t.pnlPct * 100).toFixed(2) + '%',
        t.holdBars + 'd',
      ]);

      setResults({
        totalReturn: r.metrics.totalReturn, cagr: r.metrics.cagr,
        sharpe: r.metrics.sharpe, sortino: r.metrics.sortino,
        maxDD: -r.metrics.maxDrawdown, winRate: r.metrics.winRate || 0,
        trades: r.trades.length, profitFactor: r.metrics.profitFactor || 0,
        calmar: r.metrics.calmar, var95: r.metrics.var95, cvar95: r.metrics.cvar95,
        psr: r.metrics.psr, ulcerIndex: r.metrics.ulcerIndex,
        equity: r.equity_curve.map(p => p.v),
        benchmark: bench,
        dds: stats.drawdownSeries(r.equity_curve.map(p => p.v)).map(d => d * 100),
        monthly, returns: rets,
        rollingSharpe, rollingVol,
        tradeLog,
        rawTrades: r.trades,
        walkForward,
        basket: basketMode ? { symbols: basketSymbols, weights: basketSymbols.map(s => basketWeights[s]||0), rebalance: basketRebalance } : null,
        // For persistence:
        _engineResult: r, _spec: spec, _bars: useBars,
      });

      // Persist to Supabase — prefer strategyId from selector, fall back to state
      const strat = state?.getCurrentStrategy();
      const persistStratId = strategyId || strat?.id;
      if (persistStratId) {
        await state.saveBacktestRun({
          strategy_id: persistStratId,
          asset: basketMode ? `BASKET(${basketSymbols.join('+')})` : asset,
          timeframe: '1d',
          date_start: useBars[0].time, date_end: useBars[useBars.length - 1].time,
          capital: +capital, fees_bps: fees * 100, slippage_bps: slippage * 100,
          config: {
            ...spec,
            ...(basketMode ? { basket: { symbols: basketSymbols, weights: basketSymbols.map(s => basketWeights[s]||0), rebalance: basketRebalance } } : {}),
            ...(walkForward ? { walk_forward: walkForward } : {}),
          },
          metrics: r.metrics, trades: r.trades,
          equity_curve: r.equity_curve,
        });
      }
      onToast?.(
        `Backtest complete · Sharpe ${(r.metrics.sharpe || 0).toFixed(2)}` +
        (walkForward ? ` · WF ${walkForward.overfittingFlag ? "⚠ overfit" : "OK"}` : "")
      );
      window.gqTrack?.('run_backtest', {
        asset: basketMode ? `BASKET(${basketSymbols.join('+')})` : asset,
        sharpe: r.metrics.sharpe, maxDD: r.metrics.maxDrawdown,
        trades: r.trades.length, basket: basketMode, walk_forward: !!walkForward,
      });
    } catch (e) {
      console.error('[Backtest.run]', e);
      onToast?.('Backtest failed: ' + e.message);
    }
    setRunning(false);
  };

  const runCompareStrats = async () => {
    if (!results) return;
    setCompareRunning(true);
    try {
      const { engine, data } = window.GQ;
      const bars = await data.load(asset, { bars: 1260 });
      const slice = data.sliceByDate(bars, from, to);
      const useBars = slice.length > 30 ? slice : bars;
      const newResults = {};
      for (const s of compareStrats) {
        try {
          const seed = s.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
          const fast = 7 + (seed % 6);
          const rsiT = 48 + (seed % 8);
          const cspec = {
            indicators: [
              { id:'ef', type:'ema', source:'close', params:{period: fast} },
              { id:'es', type:'ema', source:'close', params:{period: fast * 2 + 3} },
              { id:'rsi14', type:'rsi', source:'close', params:{period: 14} },
              { id:'atr14', type:'atr', source:'close', params:{period: 14} },
            ],
            entry: { long: [`ef > es and rsi14 > ${rsiT}`] },
            exit: { long: ['ef < es'] },
            sizing: { mode:'atrTarget', atrK:2, riskPct:0.01 },
            execution: { orderType:'market' },
            costs: { commissionBps:5, slippageBps:2 },
            constraints: { allowShort:false },
          };
          const r = engine.run(cspec, useBars, { capital: +capital, seed });
          newResults[s] = r.metrics;
        } catch (e) { console.warn('[Compare]', s, e); }
      }
      setCompareResults(newResults);
    } catch (e) { console.warn('[Compare.runAll]', e); }
    setCompareRunning(false);
  };

  const askAI = async () => {
    setAiLoading(true);
    try {
      if (!window.claude || !results) {
        setAiSummary("Run a backtest first, then I can analyse the results.");
      } else {
        const m = results;
        const ctx = `You are GQ AI analysing a real backtest result.
Strategy: ${strategy}  |  Period: ${from} → ${to}  |  Capital: $${capital}
Metrics:
  Total Return: ${(m.totalReturn*100).toFixed(1)}%
  CAGR:         ${(m.cagr*100).toFixed(1)}%
  Sharpe:       ${m.sharpe.toFixed(2)}
  Sortino:      ${m.sortino.toFixed(2)}
  Calmar:       ${(m.calmar||0).toFixed(2)}
  Max DD:       ${(m.maxDD*100).toFixed(1)}%
  Win rate:     ${(m.winRate*100).toFixed(1)}%
  Profit Factor:${m.profitFactor.toFixed(2)}
  VaR 95%:      ${(m.var95*100).toFixed(2)}%
  CVaR 95%:     ${(m.cvar95*100).toFixed(2)}%
  Ulcer Idx:    ${(m.ulcerIndex||0).toFixed(3)}
  PSR:          ${(m.psr||0).toFixed(2)}
  Trades:       ${m.trades}

Give 3-4 bullet analysis: strengths, weaknesses, and 2 specific parameter suggestions. Under 120 words. Reference the actual numbers above.`;
        const r = await window.claude.complete(ctx);
        setAiSummary(r);
      }
    } catch (e) {
      setAiSummary("AI service unavailable. Try again.");
    }
    setAiLoading(false);
  };

  const applyPreset=p=>{setFrom(p.from);setTo(p.to);onToast?.(`Applied "${p.label}" preset`);};

  const R=results;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <PageHdr icon={Play} iconColor="#3b9eff" iconBg="rgba(59,158,255,0.10)"
        title="Backtest" subtitle="Test strategies across any historical period with institutional-grade metrics."
        action={<div style={{display:"flex",gap:6,alignItems:"center"}}>
          {window.PipelineContextChip && <PipelineContextChip page="backtest"/>}
          <button className="btn btn-ghost btn-sm" onClick={()=>setCompareMode(c=>!c)}><GitMerge size={11}/>{compareMode?" Exit compare":" Compare"}</button>
          <button className="btn btn-primary btn-sm" onClick={running?undefined:run} style={{opacity:running?.7:1}}>
            {running?<><RefreshCw size={11} style={{animation:"spin 1s linear infinite"}}/> Running…</>:<><Play size={11}/> Run backtest</>}
          </button>
        </div>}
      />

      {/* Config */}
      <div style={{display:"grid",gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",gap:12}}>
        <div className="card">
          <div className="card-title" style={{display:"flex",justifyContent:"space-between"}}>
            <span>Configuration</span>
            <button className="btn btn-ghost btn-sm" style={{padding:"2px 8px",fontSize:10,color:"#D946EF",borderColor:"rgba(217,70,239,0.2)"}} onClick={()=>onToast?.("GQ AI: Try extending to 2020 for more regime coverage")}><Brain size={10}/> GQ Suggest</button>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"grid",gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",gap:8}}>
              <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Strategy</div>
                <select className="input" value={strategyId || ''} onChange={e => {
                  const id = e.target.value;
                  const s = userStrategies.find(x => x.id === id);
                  setStrategyId(id);
                  setStrategy(s?.name || '');
                  if (s?.current_params) setStrategySpec(s.current_params);
                }}>
                  {userStrategies.length === 0
                    ? <option value="">No strategies — go to Lab</option>
                    : userStrategies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)
                  }
                </select>
              </div>
              <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Asset (market data)</div>
                <select className="input" value={asset} onChange={e=>setAsset(e.target.value)}>
                  {availableSymbols.map(s => <option key={s} value={s}>{s}{BUNDLED_SYMBOLS.includes(s) ? '' : ' (uploaded)'}</option>)}
                </select></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",gap:8}}>
              <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>From</div><input className="input" type="date" value={from} onChange={e=>setFrom(e.target.value)}/></div>
              <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>To</div><input className="input" type="date" value={to} onChange={e=>setTo(e.target.value)}/></div>
            </div>
            <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Starting capital ($)</div><input className="input" type="number" value={capital} onChange={e=>setCapital(+e.target.value)}/></div>
          </div>
        </div>
        <div className="card">
          <div className="card-title">Presets & settings</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
            {BT_PRESETS.map(p=><button key={p.label} className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>applyPreset(p)}>{p.label}</button>)}
          </div>
          <div style={{display:"grid",gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",gap:8}}>
            <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Fees (%)</div><input className="input" type="number" step="0.01" value={fees} onChange={e=>setFees(+e.target.value)}/></div>
            <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Slippage (%)</div><input className="input" type="number" step="0.01" value={slippage} onChange={e=>setSlippage(+e.target.value)}/></div>
          </div>
          {showAdvanced&&<div style={{marginTop:10,paddingTop:10,borderTop:"1px solid var(--border)",display:"grid",gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",gap:8}}>
            <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Benchmark</div><select className="input"><option>BTC</option><option>SPY</option><option>None</option></select></div>
            <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>Timeframe</div><select className="input"><option>1D</option><option>4H</option><option>1H</option></select></div>
          </div>}
          <button className="btn btn-ghost btn-sm" style={{marginTop:8,fontSize:10}} onClick={()=>setShowAdvanced(a=>!a)}>{showAdvanced?"Hide":"Show"} advanced</button>
        </div>
      </div>

      {/* Walk-forward & basket — second config row */}
      <div style={{display:"grid",gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",gap:12}}>
        {/* Walk-forward */}
        <div className="card">
          <div className="card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>Walk-forward cross-validation</span>
            <label style={{display:"flex",alignItems:"center",gap:6,fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",cursor:basketMode?"not-allowed":"pointer",opacity:basketMode?0.5:1}}>
              <input type="checkbox" checked={wfEnabled} disabled={basketMode} onChange={e=>setWfEnabled(e.target.checked)}/>
              {wfEnabled?"Enabled":"Disabled"}
            </label>
          </div>
          <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--subtle)",marginBottom:10,lineHeight:1.5}}>
            Split the period into folds and run train/test on each — flags overfitting when in-sample Sharpe far exceeds out-of-sample.
          </div>
          <div style={{display:"grid",gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",gap:8,opacity:wfEnabled?1:0.55}}>
            <div>
              <div style={{display:"flex",justifyContent:"space-between",fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>
                <span>Folds</span><span style={{fontFamily:"var(--font-mono)"}}>{wfFolds}</span>
              </div>
              <input type="range" min="3" max="10" step="1" value={wfFolds} disabled={!wfEnabled} onChange={e=>setWfFolds(+e.target.value)} style={{width:"100%"}}/>
            </div>
            <div>
              <div style={{display:"flex",justifyContent:"space-between",fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:4}}>
                <span>Train fraction</span><span style={{fontFamily:"var(--font-mono)"}}>{(wfTrainPct*100).toFixed(0)}%</span>
              </div>
              <input type="range" min="0.5" max="0.9" step="0.05" value={wfTrainPct} disabled={!wfEnabled} onChange={e=>setWfTrainPct(+e.target.value)} style={{width:"100%"}}/>
            </div>
          </div>
          {basketMode && <div style={{marginTop:8,fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)"}}>Walk-forward is only available in single-asset mode.</div>}
        </div>

        {/* Multi-asset basket */}
        <div className="card">
          <div className="card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>Asset mode</span>
            <div style={{display:"flex",gap:0,background:"var(--bg-deep)",borderRadius:"var(--r-md)",padding:2}}>
              {["single","basket"].map(m=>(
                <button key={m} onClick={()=>setBasketMode(m==="basket")} style={{
                  background:(m==="basket")===basketMode?"var(--primary-dim)":"transparent",
                  border:"none",padding:"4px 10px",borderRadius:"var(--r-sm)",
                  cursor:"pointer",fontFamily:"var(--font-ui)",fontSize:11,
                  color:(m==="basket")===basketMode?"var(--primary)":"var(--muted)",
                  fontWeight:(m==="basket")===basketMode?600:400,
                }}>{m==="single"?"Single asset":"Basket"}</button>
              ))}
            </div>
          </div>
          {!basketMode && (
            <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--subtle)",lineHeight:1.5}}>
              Trade signals on a single asset. To diversify across multiple instruments, switch to <b style={{color:"var(--muted)"}}>Basket</b>.
            </div>
          )}
          {basketMode && (
            <>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                {availableSymbols.map(s=>(
                  <button key={s} onClick={()=>{
                    setBasketSymbols(prev=>{
                      const next = prev.includes(s) ? prev.filter(x=>x!==s) : [...prev, s];
                      // Equal-weight any newly added; preserve existing tweaks
                      const eq = 1 / Math.max(1, next.length);
                      const w = {...basketWeights};
                      next.forEach(sym => { if (w[sym] == null) w[sym] = eq; });
                      Object.keys(w).forEach(k => { if (!next.includes(k)) delete w[k]; });
                      setBasketWeights(w);
                      return next;
                    });
                  }} style={{
                    background: basketSymbols.includes(s)?"rgba(59,158,255,0.12)":"transparent",
                    border:`1px solid ${basketSymbols.includes(s)?"rgba(59,158,255,0.35)":"var(--border-strong)"}`,
                    borderRadius:"var(--r-full)",padding:"3px 10px",cursor:"pointer",
                    fontFamily:"var(--font-ui)",fontSize:11,
                    color: basketSymbols.includes(s)?"#3b9eff":"var(--muted)",
                  }}>{basketSymbols.includes(s)?"✓ ":""}{s}</button>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8,marginBottom:8}}>
                {basketSymbols.map(s=>(
                  <div key={s}>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--muted)",marginBottom:2}}>{s} weight</div>
                    <input className="input" type="number" min="0" max="1" step="0.05"
                      value={basketWeights[s] ?? 0}
                      onChange={e=>setBasketWeights(w=>({...w,[s]:Math.max(0,Math.min(1,+e.target.value))}))}
                      style={{padding:"4px 6px",fontSize:11}}/>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <div style={{fontFamily:"var(--font-mono)",fontSize:10,color:(()=>{
                  const sum = basketSymbols.reduce((a,s)=>a+(basketWeights[s]||0),0);
                  return Math.abs(sum-1) < 0.001 ? "var(--green)" : "var(--orange)";
                })()}}>
                  Σ = {basketSymbols.reduce((a,s)=>a+(basketWeights[s]||0),0).toFixed(2)}{Math.abs(basketSymbols.reduce((a,s)=>a+(basketWeights[s]||0),0)-1) > 0.001 ? " (auto-normalised on run)" : ""}
                </div>
                <select className="input" value={basketRebalance} onChange={e=>setBasketRebalance(e.target.value)} style={{maxWidth:120,fontSize:11,padding:"4px 6px"}}>
                  <option value="daily">Daily rebalance</option>
                  <option value="weekly">Weekly rebalance</option>
                  <option value="monthly">Monthly rebalance</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Empty / Loading / Results */}
      {!R&&!running&&<div className="card" style={{display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:10,minHeight:200}}>
        <Play size={26} color="var(--subtle)"/><div style={{fontFamily:"var(--font-ui)",fontSize:13,color:"var(--subtle)"}}>Configure and run to see results</div>
      </div>}
      {running&&<div className="card" style={{display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:10,minHeight:200}}>
        <RefreshCw size={20} color="#3b9eff" style={{animation:"spin 1s linear infinite"}}/>
        <div style={{fontFamily:"var(--font-ui)",fontSize:13,color:"var(--muted)"}}>Replaying {Math.round((new Date(to)-new Date(from))/86400000)} days of {strategy}…</div>
      </div>}

      {R&&<>
        {/* Result tabs */}
        <div style={{display:"flex",gap:0,borderBottom:"1px solid var(--border)"}}>
          {[["overview","Overview"],["charts","Charts"],["trades","Trades"],["rolling","Rolling"],["compare","Compare"]].map(([id,label])=>(
            <button key={id} onClick={()=>setResultTab(id)} style={{background:"none",border:"none",borderBottom:`2px solid ${resultTab===id?"#3b9eff":"transparent"}`,padding:"8px 16px",cursor:"pointer",fontFamily:"var(--font-ui)",fontSize:12,fontWeight:resultTab===id?600:400,color:resultTab===id?"#3b9eff":"var(--muted)",transition:"all .15s"}}>{label}</button>
          ))}
        </div>

        {resultTab==="overview"&&<>
          {/* KPI Strip */}
          <div style={{display:"grid",gridTemplateColumns: narrow ? "repeat(2, 1fr)" : "repeat(4,1fr)",gap:8}}>
            <StatCard title="Return" value={`+${(R.totalReturn*100).toFixed(1)}%`} accent="green"/>
            <StatCard title="Sharpe" value={R.sharpe.toFixed(2)} accent="purple"/>
            <StatCard title="Max DD" value={`${(R.maxDD*100).toFixed(1)}%`} accent="red"/>
            <StatCard title="Trades" value={R.trades}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns: narrow ? "repeat(2, 1fr)" : "repeat(4,1fr)",gap:8}}>
            {[{t:"CAGR",v:`+${(R.cagr*100).toFixed(1)}%`,c:"green"},{t:"Sortino",v:R.sortino.toFixed(2),c:"purple"},{t:"Win rate",v:`${(R.winRate*100).toFixed(1)}%`},{t:"Profit factor",v:R.profitFactor.toFixed(2)}].map(s=>(
              <StatCard key={s.t} title={s.t} value={s.v} accent={s.c||"default"}/>
            ))}
          </div>
          {/* Equity curve */}
          <div className="card">
            <div className="card-title" style={{display:"flex",justifyContent:"space-between"}}>
              <span>Equity curve vs benchmark</span>
              <span style={{fontFamily:"var(--font-mono)",fontSize:10,color:"var(--muted)",display:"flex",gap:12}}>
                <span style={{color:"#11ff99"}}>— Strategy</span><span style={{color:"#D946EF"}}>--- Benchmark</span>
              </span>
            </div>
            {window.EquityChart&&<EquityChart equity={R.equity} benchmark={R.benchmark} height={240}/>}
          </div>

          {/* Walk-forward results */}
          {R.walkForward && <WalkForwardPanel wf={R.walkForward}/>}

          {/* Basket composition (read-only) */}
          {R.basket && (
            <div className="card" style={{background:"rgba(59,158,255,0.04)",border:"1px solid rgba(59,158,255,0.18)"}}>
              <div className="card-title" style={{display:"flex",justifyContent:"space-between"}}>
                <span>Basket composition</span>
                <span className="badge badge-cyan" style={{fontSize:10}}>{R.basket.rebalance}</span>
              </div>
              <div style={{display:"flex",gap:18,flexWrap:"wrap"}}>
                {R.basket.symbols.map((s,i)=>(
                  <div key={s}>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)"}}>{s}</div>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:13,color:"#3b9eff"}}>{((R.basket.weights[i]||0)*100).toFixed(1)}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* AI Summary */}
          <div className="card" style={{background:"rgba(217,70,239,0.03)",border:"1px solid rgba(217,70,239,0.15)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontFamily:"'Inter Tight','Inter',sans-serif",fontSize:14,fontWeight:800,color:"#D946EF",letterSpacing:"-0.02em"}}>GQ</span>
                <span style={{fontFamily:"var(--font-ui)",fontSize:13,fontWeight:600,color:"var(--ink)"}}>AI Analysis</span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={askAI} disabled={aiLoading} style={{color:"#D946EF",borderColor:"rgba(217,70,239,0.3)"}}><Brain size={11}/>{aiSummary?"Re-analyse":"Analyse"}</button>
            </div>
            {!aiSummary&&!aiLoading&&<div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)"}}>Click "Analyse" for AI-powered insights, weakness detection, and parameter suggestions.</div>}
            {aiLoading&&<div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)"}}>GQ™ analysing results…</div>}
            {aiSummary&&!aiLoading&&<div style={{fontFamily:"var(--font-ui)",fontSize:13,color:"var(--body)",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{aiSummary}</div>}
          </div>
        </>}

        {resultTab==="charts"&&<>
          <div style={{display:"grid",gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",gap:12}}>
            <div className="card"><div className="card-title">Drawdown (underwater)</div>{window.DrawdownChart&&<DrawdownChart dds={R.dds} height={200}/>}</div>
            <div className="card"><div className="card-title">Return distribution</div>{window.DistributionChart&&<DistributionChart returns={R.returns} height={200}/>}</div>
          </div>
          <div className="card"><div className="card-title">Monthly returns heatmap</div>{window.MonthlyHeatmapChart&&<MonthlyHeatmapChart monthly={R.monthly} height={200}/>}</div>
          <Expandable title="Win/loss streaks"><StreakChart trades={R.rawTrades} height={160}/></Expandable>
        </>}

        {resultTab==="trades"&&<div className="card">
          <div className="card-title">Trade log · last {Math.min(30, R.tradeLog?.length || 0)} of {R.trades}</div>
          {R.tradeLog && R.tradeLog.length > 0 ? (
            <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:"1px solid var(--border)"}}>
              {["#","Date","Side","Entry","Exit","P&L","P&L %","Hold"].map(h=><th key={h} style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--muted)",textAlign:"left",padding:"4px 8px",paddingBottom:8,fontWeight:500,letterSpacing:".04em"}}>{h.toUpperCase()}</th>)}
            </tr></thead><tbody>
              {R.tradeLog.map((r,i)=>{const pnl=parseFloat(r[5]);return(
                <tr key={i} style={{borderBottom:i<R.tradeLog.length-1?"1px solid rgba(255,255,255,0.04)":"none"}}>
                  <td style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)",padding:"7px 8px"}}>{r[0]}</td>
                  <td style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)",padding:"7px 8px"}}>{r[1]}</td>
                  <td style={{padding:"7px 8px"}}><span className={`badge ${r[2]==="Long"?"badge-green":"badge-red"}`} style={{fontSize:10}}>{r[2]}</span></td>
                  <td style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--body)",padding:"7px 8px"}}>${r[3]}</td>
                  <td style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--body)",padding:"7px 8px"}}>${r[4]}</td>
                  <td style={{fontFamily:"var(--font-mono)",fontSize:11,color:pnl>=0?"var(--green)":"var(--red)",padding:"7px 8px",fontWeight:500}}>{r[5]}</td>
                  <td style={{fontFamily:"var(--font-mono)",fontSize:11,color:pnl>=0?"var(--green)":"var(--red)",padding:"7px 8px"}}>{r[6]}</td>
                  <td style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)",padding:"7px 8px"}}>{r[7]}</td>
                </tr>);})}
            </tbody></table></div>
          ) : (
            <div style={{padding:"24px 0",textAlign:"center",fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)"}}>
              {R.basket ? "Basket mode compounds weighted returns — no individual trades to log." : "No trades executed in this period."}
            </div>
          )}
        </div>}

        {resultTab==="rolling"&&<>
          <div className="card"><div className="card-title">Rolling Sharpe ratio (20-period)</div><RollingChart data={R.rollingSharpe} label="Sharpe" color="#D946EF" height={200}/></div>
          <div className="card"><div className="card-title">Rolling volatility (%)</div><RollingChart data={R.rollingVol} label="Vol" color="#3b9eff" height={200}/></div>
        </>}

        {resultTab==="compare"&&<>
          <div className="card">
            <div className="card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>Multi-strategy comparison</span>
              <button className="btn btn-ghost btn-sm" onClick={runCompareStrats} disabled={compareRunning||!results} style={{fontSize:11}}>
                {compareRunning?<><RefreshCw size={10} style={{animation:"spin 1s linear infinite"}}/> Running…</>:<><Play size={10}/> Run all</>}
              </button>
            </div>
            <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
              {userStrategies.filter(s=>s.id!==strategyId).map(s=>(
                <button key={s.id} onClick={()=>setCompareStrats(p=>p.includes(s.name)?p.filter(x=>x!==s.name):[...p,s.name])} style={{
                  background:compareStrats.includes(s.name)?"rgba(59,158,255,0.1)":"transparent",border:`1px solid ${compareStrats.includes(s.name)?"rgba(59,158,255,0.3)":"var(--border-strong)"}`,borderRadius:"var(--r-full)",padding:"4px 12px",cursor:"pointer",fontFamily:"var(--font-ui)",fontSize:11,color:compareStrats.includes(s.name)?"#3b9eff":"var(--body)"
                }}>{compareStrats.includes(s.name)?"✓ ":""}{s.name}</button>
              ))}
            </div>
            {window.EquityChart&&<EquityChart equity={R.equity} benchmark={R.benchmark} height={240}/>}
            <div style={{marginTop:14,overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:"1px solid var(--border)"}}>
              {["Strategy","Return","Sharpe","Max DD","Win Rate","Trades"].map(h=><th key={h} style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--muted)",textAlign:"left",padding:"6px 10px",fontWeight:500}}>{h}</th>)}
            </tr></thead><tbody>
              <tr style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                <td style={{fontFamily:"var(--font-ui)",fontSize:12,fontWeight:600,color:"var(--ink)",padding:"8px 10px"}}>{strategy}</td>
                <td style={{fontFamily:"var(--font-mono)",fontSize:12,color:"var(--green)",padding:"8px 10px"}}>+{(R.totalReturn*100).toFixed(1)}%</td>
                <td style={{fontFamily:"var(--font-mono)",fontSize:12,color:"#D946EF",padding:"8px 10px"}}>{R.sharpe.toFixed(2)}</td>
                <td style={{fontFamily:"var(--font-mono)",fontSize:12,color:"var(--red)",padding:"8px 10px"}}>-{(R.maxDD*100).toFixed(1)}%</td>
                <td style={{fontFamily:"var(--font-mono)",fontSize:12,padding:"8px 10px"}}>{(R.winRate*100).toFixed(1)}%</td>
                <td style={{fontFamily:"var(--font-mono)",fontSize:12,padding:"8px 10px"}}>{R.trades}</td>
              </tr>
              {compareStrats.map(s=>{
                const cm = compareResults[s];
                return (
                  <tr key={s} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                    <td style={{fontFamily:"var(--font-ui)",fontSize:12,fontWeight:500,color:"var(--body)",padding:"8px 10px"}}>{s}</td>
                    {cm ? (
                      <>
                        <td style={{fontFamily:"var(--font-mono)",fontSize:12,color:(cm.totalReturn||0)>=0?"var(--green)":"var(--red)",padding:"8px 10px"}}>{(cm.totalReturn||0)>=0?"+":""}{((cm.totalReturn||0)*100).toFixed(1)}%</td>
                        <td style={{fontFamily:"var(--font-mono)",fontSize:12,color:"#D946EF",padding:"8px 10px"}}>{(cm.sharpe||0).toFixed(2)}</td>
                        <td style={{fontFamily:"var(--font-mono)",fontSize:12,color:"var(--red)",padding:"8px 10px"}}>-{Math.abs((cm.maxDrawdown||0)*100).toFixed(1)}%</td>
                        <td style={{fontFamily:"var(--font-mono)",fontSize:12,padding:"8px 10px"}}>{((cm.winRate||0)*100).toFixed(1)}%</td>
                        <td style={{fontFamily:"var(--font-mono)",fontSize:12,padding:"8px 10px"}}>{cm.tradeCount||'—'}</td>
                      </>
                    ) : (
                      <td colSpan={5} style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--subtle)",padding:"8px 10px",fontStyle:"italic"}}>
                        {compareRunning ? "Running…" : "Click \"Run all\" to compare"}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody></table></div>
          </div>
        </>}

        {/* Pipeline CTAs */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button className="btn btn-primary btn-sm" onClick={()=>onNavigate?.("optimize")}><Cpu size={11}/> Optimize this →</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate?.("stress")}><Shield size={11}/> Stress test →</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate?.("tearsheet")}><FileText size={11}/> Full tearsheet</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>onToast?.("Minted to vault")}><Plus size={11}/> Mint to vault</button>
        </div>
      </>}
    </div>
  );
}
window.BacktestPage=BacktestPage;
